import React, { CSSProperties, forwardRef, MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { MandatoryChildren } from '../../../models/children-props';
import { Portal } from '../Portal';
import { idGenerator } from '../../../helpers/idGenerator';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { useDimensions } from '../../../hooks/useDimensions';
import { BreakpointsValuesEnum } from '../../../constants/breakpoints-values';
import { dropdownDirection } from '../../../helpers/dropdownDirection';
import { useForwardedRefTransformer } from '../../../hooks/useForwardedRef';
import { Property } from 'csstype';

export type DropdownWrapperViewProps = MandatoryChildren<{
  //
  component?: string;
  expanded: boolean;
  portalWrapperRef: MutableRefObject<HTMLElement | null>;
  side?: 'left' | 'right';
  id?: string;
  className?: string;
  baseZIndex?: Property.ZIndex;
}>;

const initialPortalStyles: CSSProperties = {
  zIndex: scssVars['z-index-overmodal'],
  position: 'absolute',
  left: '-9999em',
  top: '-9999em',
  display: 'flex',
  visibility: 'hidden'
};

export const DropdownWrapperView = forwardRef<HTMLDivElement, DropdownWrapperViewProps>((props, ref): JSX.Element => {
  const { expanded, portalWrapperRef, id, className, component, children, side, baseZIndex } = props;
  const inputID = useMemo(() => id ?? idGenerator('portal-element-dropdown'), [id]);
  const portalElementRef = useForwardedRefTransformer(ref);
  const bodyRef = useRef<HTMLElement>(document.body);
  const { height: viewportHeight } = useDimensions(bodyRef);
  const dropdownContainerHeightType =
    viewportHeight && viewportHeight <= BreakpointsValuesEnum.tabletportrait ? 'mobile' : 'desktop';
  const [portalStyles, setPortalStyles] = useState<CSSProperties>(initialPortalStyles);
  const portalDropdownDirection =
    portalWrapperRef.current && portalElementRef.current && expanded
      ? dropdownDirection(portalWrapperRef.current, portalElementRef.current, dropdownContainerHeightType)
      : null;

  // recalculate styles
  useEffect(() => {
    if (!expanded || !portalWrapperRef.current || !portalElementRef.current || !portalDropdownDirection) {
      setPortalStyles(initialPortalStyles);
      return;
    }
    const screenHeight = document.documentElement.clientHeight;
    const screenWidth = document.documentElement.clientWidth;
    const { left, top, width: initialWidth, right } = portalWrapperRef.current.getBoundingClientRect();
    const refElHeight = portalWrapperRef.current.offsetHeight;
    const portalelementHeight = portalElementRef.current.clientHeight;
    const portalelementWidth = portalElementRef.current.offsetWidth;
    const calcualtedTopDown = top + 4 + refElHeight;
    const calcualtedTopUp = top - 4 - portalelementHeight;
    const calcualtedBottomUp = screenHeight - top + 4;
    const maxTopDown = calcualtedTopDown >= 0 ? calcualtedTopDown : 0;
    const maxTopUp = calcualtedTopUp >= 0 ? calcualtedTopUp : 0;
    const maxBottomDown = top + 4 + refElHeight + portalelementHeight <= screenHeight;
    const maxBottomUp = calcualtedBottomUp >= 0 ? calcualtedBottomUp : 0;
    const width =
      initialWidth > portalelementWidth
        ? initialWidth
        : portalelementWidth > 300
        ? Math.max(300, initialWidth)
        : Math.max(initialWidth, portalelementWidth);
    setPortalStyles({
      ...initialPortalStyles,
      ...(side === 'right'
        ? { right: screenWidth - right, left: 'unset', justifyContent: 'right' }
        : { left, right: 'unset' }),
      ...(portalDropdownDirection === 'down'
        ? { ...(maxBottomDown ? { top: maxTopDown } : { bottom: 0, top: 'unset' }) }
        : { bottom: maxBottomUp, top: maxTopUp }),
      width,
      visibility: 'unset',
      ...(baseZIndex ? { zIndex: baseZIndex } : {})
    });
  }, [expanded, portalDropdownDirection, side, portalWrapperRef, portalElementRef, baseZIndex]);

  const getProps = () => ({
    style: portalStyles,
    id: inputID,
    ...(ref ? { ref } : { ref: portalElementRef }),
    ...(className ? { className } : {})
  });

  return <Portal>{React.createElement(component ?? 'div', getProps(), children)}</Portal>;
});

DropdownWrapperView.displayName = 'DropdownWrapperView';
